import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { Dialog, Grid, Slide, styled, Switch, SwitchProps, TextField, useMediaQuery } from '@mui/material';
import { TransitionProps } from '@mui/material/transitions';
import _cloneDeep from 'lodash/cloneDeep';
import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';
import React, { useEffect, useMemo, useState } from 'react';
import { NavigateFunction } from 'react-router';
import { searchProductsRest } from '../../api';
import brandclub_colors from '../../colors/brandclubColors';
import { AppCheckbox } from '../../Components/AppCheckbox';
import { Button } from '../../Components/Button';
import { SearchIcon } from '../../icons/SearchIcon';
import { brandclubStyles } from '../../styles';
import {
  FilterParam,
  FilterParamKey,
  FilterParams,
  getFilterParamKeyFromValue,
  SearchFilterResponse,
} from '../../types/filters';
import { TABLET_SCREEN_SIZE } from '../../utils/constants';
import { searchParamsToSearchFilters } from '../SearchResults/searchResultsUtils';
import { BaseComponentPropsType } from '../type';

const SHOW_MORE_INITIAL_LIMIT = 6;
const SHOW_MORE_LIMIT = 100;

type FilterValue = {
  name: string | number;
  displayName: string;
  enable: boolean;
};

type FilterState = {
  [K in FilterParamKey]?: FilterValue[];
};

const BrandclubFilterSwitch = styled((props: SwitchProps) => (
  <Switch
    checkedIcon={
      <div
        style={{
          width: 14,
          height: 14,
          borderRadius: 100,
          backgroundColor: '#fff',
          alignItems: 'center',
          display: 'flex',
          justifyContent: 'center',
        }}
      >
        <CheckRoundedIcon
          sx={(theme) => ({
            color: theme.palette.primary.main,
            width: 12,
            height: 12,
          })}
        />
      </div>
    }
    focusVisibleClassName=".Mui-focusVisible"
    disableRipple
    {...props}
  />
))(({ theme }) => ({
  width: 30,
  height: 20,
  padding: 0,
  '& .MuiSwitch-switchBase': {
    padding: 0,
    margin: 3,
    transitionDuration: '300ms',
    '&.Mui-checked': {
      transform: 'translateX(10px)',
      color: '#fff',
      '& + .MuiSwitch-track': {
        backgroundColor: theme.palette.primary.main,
        opacity: 1,
        border: 0,
      },
      '&.Mui-disabled + .MuiSwitch-track': {
        opacity: 0.5,
      },
    },
    '&.Mui-focusVisible .MuiSwitch-thumb': {
      color: '#33cf4d',
      border: '6px solid #fff',
    },
    '&.Mui-disabled .MuiSwitch-thumb': {
      color: 'grey',
    },
    '&.Mui-disabled + .MuiSwitch-track': {
      opacity: theme.palette.mode === 'light' ? 0.7 : 0.3,
    },
  },
  '& .MuiSwitch-thumb': {
    boxSizing: 'border-box',
    width: 14,
    height: 14,
    boxShadow: 'none',
  },
  '& .MuiSwitch-track': {
    borderRadius: 100,
    backgroundColor: theme.palette.mode === 'light' ? '#E9E9EA' : '#39393D',
    opacity: 1,
    transition: theme.transitions.create(['background-color'], {
      duration: 300,
    }),
  },
}));

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement;
  },
  ref: React.Ref<unknown>,
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const FilterHeader = styled(Grid)(({ theme }) => ({
  fontFamily: 'Poppins',
  color: theme.palette.primary.main,
  padding: '21px 30px',
  display: 'grid',
  gridTemplateColumns: '50px auto 50px',
  width: 690,
  borderBottom: `1px solid ${brandclub_colors.blueWhite}`,
  [`@media screen and (max-width: ${TABLET_SCREEN_SIZE}px)`]: {
    width: '100%',
    '.center': { fontSize: 16 },
    '.right': {
      fontSize: 14,
    },
  },
  '.left': {
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'center',
  },
  '.center': {
    fontSize: 18,
    fontWeight: 600,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  '.right': {
    fontSize: 16,
    fontWeight: 500,
    color: theme.palette.primary.main,
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    cursor: 'pointer',
  },
}));

const FilterFooter = styled(Grid)(({ theme }) => ({
  fontFamily: 'Poppins',
  color: theme.palette.primary.main,
  padding: '20px 0',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderTop: `1px solid ${brandclub_colors.blueWhite}`,
  '.apply_button': {
    fontFamily: 'Poppins',
    padding: '12px 100px',
    outline: 'none',
    border: 'none',
    background: theme.palette.primary.main,
    color: '#fff',
    borderRadius: 999,
    fontSize: 16,
    fontWeight: 500,
  },
}));

const FilterContentContainer = styled(Grid)(({ theme }) => ({
  color: theme.palette.primary.main,
  fontSize: 16,
  fontWeight: 500,
  display: 'flex',
  flexDirection: 'column',
  padding: '0 30px',
  width: 690,
  overflowY: 'scroll',
  ...brandclubStyles.customScrollbar,
  [`@media screen and (max-width: ${TABLET_SCREEN_SIZE}px)`]: {
    width: '100%',
    fontSize: 14,
  },
  '.mobile_search_bar': {
    display: 'none',
    [`@media screen and (max-width: ${TABLET_SCREEN_SIZE}px)`]: {
      display: 'block',
    },
  },
  '.row': {
    borderBottom: `1px solid ${brandclub_colors.blueWhite}`,
    padding: '30px 0px',
    '&:las-child': {
      borderBottom: `none`,
    },
    '.row_title': {
      fontSize: 20,
      fontWeight: 600,
      marginBottom: 13,
      [`@media screen and (max-width: ${TABLET_SCREEN_SIZE}px)`]: {
        fontSize: 14,
      },
    },
    '.row_options': {
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      [`@media screen and (max-width: ${TABLET_SCREEN_SIZE}px)`]: {
        gridTemplateColumns: '1fr',
      },
      gap: '8px',
      '.show_more': {
        textDecoration: 'underline',
        cursor: 'pointer',
      },
      '.option': {
        display: 'flex',
        alignItems: 'flex-start',
        '.option_label': {
          wordBreak: 'break-word',
          paddingTop: '9px',
        },
      },
      '.option_reward': {
        display: 'flex',
        flexWrap: 'nowrap',
        [`@media screen and (max-width: ${TABLET_SCREEN_SIZE}px)`]: {
          gridColumnStart: 1,
          gridRowStart: 1,
        },
        '&.right': {
          gridColumnStart: 1,
          gridRowStart: 1,
          gridRowEnd: 3,
          [`@media screen and (max-width: ${TABLET_SCREEN_SIZE}px)`]: {
            gridColumnStart: 1,
            gridRowStart: 1,
          },
        },

        '.toggle_text': {
          marginLeft: 11,
          maxWidth: 200,
        },
      },
    },
  },
}));

const FilterDialogContainer = styled('div')({
  height: '100%',
  display: 'grid',
  gridTemplateRows: '70px 1fr 83px',
});

const getStaticFilters = () =>
  ({
    RetailPrice: [
      { name: '0to25', displayName: '$0 - $25', enable: false },
      { name: '25to50', displayName: '$25 - $50', enable: false },
      { name: '50to100', displayName: '$50 - $100', enable: false },
      { name: 'over100', displayName: 'Over $100', enable: false },
    ],
    CampaignTypes: [
      {
        name: 'purchaseRewardCampaign',
        displayName: 'Purchase rewards',
        enable: false,
      },
      {
        name: 'affiliateLinkCampaign',
        displayName: 'Affiliate rewards',
        enable: false,
      },
    ],
  } satisfies FilterState);

const getDynamicFilters = (facetsResult: SearchFilterResponse): FilterState => {
  const res: FilterState = {};
  const categoryFacets = _get(facetsResult, ['facets', 'categoryId'], []);
  const subCategoryFacets = _get(facetsResult, ['facets', 'subCategoryId'], []);
  const retailerFacets = _get(facetsResult, ['facets', 'retailerId'], []);
  const brandFacets = _get(facetsResult, ['facets', 'brandId'], []);

  if (!_isEmpty(categoryFacets)) {
    res.CategoryIds = categoryFacets.map((category) => ({
      name: category.categoryId,
      displayName: category.name,
      enable: false,
    }));
  }

  if (!_isEmpty(subCategoryFacets)) {
    res.SubcategoryIds = subCategoryFacets.map((subCategory) => ({
      name: subCategory.subCategoryId,
      displayName: subCategory.name,
      enable: false,
    }));
  }

  if (!_isEmpty(retailerFacets)) {
    res.RetailerIds = retailerFacets.map((retailer) => ({
      name: retailer.retailerId,
      displayName: retailer.name,
      enable: false,
    }));
  }

  if (!_isEmpty(brandFacets)) {
    res.BrandIds = brandFacets.map((brand) => ({
      name: brand.brandId,
      displayName: brand.name,
      enable: false,
    }));
  }

  return res;
};

const convertFilterStateToSearchParams = (filterState: FilterState): string => {
  let searchRoute = '';

  Object.entries(filterState).forEach(([filterName, filterValues]) => {
    filterValues?.forEach((option) => {
      if (option.enable) {
        searchRoute += `&${FilterParam[filterName as keyof FilterState]}=${option.name}`;
      }
    });
  });

  return searchRoute;
};

const convertSearchParamToFilterState = (searchParams: URLSearchParams, filterState: FilterState): FilterState => {
  const params: FilterParams = {};
  searchParams.forEach((value, key) => {
    const decodedKey = decodeURIComponent(key);
    const decodedValue = decodeURIComponent(value);
    const filterKey = getFilterParamKeyFromValue(decodedKey);
    if (!filterKey) return;
    if (filterKey !== 'VersionId' && filterKey !== 'SiteId') {
      params[filterKey] = params[filterKey] || [];
      params[filterKey]?.push(decodedValue);
    } else {
      params[filterKey] = decodedValue;
    }
  });

  const newFilterState = _cloneDeep(filterState);
  Object.entries(newFilterState).forEach(([filterName, filterValues]) => {
    filterValues?.forEach((state) => {
      if (params[filterName as keyof FilterParams]?.includes(`${state.name}`)) {
        state.enable = true;
      }
    });
  });

  return newFilterState;
};

const SelectOption: React.FC<{
  checked?: boolean;
  displayName?: string;
  onChange: (event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => void;
}> = ({ checked = false, displayName = '', onChange }) => (
  <div className="option">
    <AppCheckbox
      checkboxProps={{
        checked,
        sx: (theme) => ({
          padding: '0px 19px 0px 0px',
          marginLeft: '-2px',
          '&.Mui-checked': {
            color: theme.palette.primary.main,
          },
        }),
        onChange,
      }}
      label=""
      hideLabel
    />
    <div className="option_label">{displayName}</div>
  </div>
);

const FilterRow: React.FC<{
  children?: React.ReactNode;
  title: string;
  options: FilterValue[];
  hideOptions?: boolean;
  onChange: (name: string | number, enabled: boolean) => void;
}> = ({ title, options, onChange, ...props }) => {
  return (
    <div className="row">
      <div className="row_title">{title}</div>
      <div className="row_options">
        {!props.hideOptions &&
          options.map((option) => {
            return (
              <SelectOption
                onChange={(e, checked) => {
                  onChange(option.name, checked);
                }}
                key={option.name}
                checked={option?.enable}
                displayName={option?.displayName}
              />
            );
          })}
        {props.children}
      </div>
    </div>
  );
};

const ShowMore: React.FC<{
  onClick: (e: React.MouseEvent) => void;
  className?: string;
}> = ({ onClick, className }) => (
  <Button
    onClick={onClick}
    className={[className, 'show_more'].filter(Boolean).join(' ')}
    variant="text"
    sx={{
      width: 'fit-content',
      fontWeight: 500,
    }}
  >
    Show more
  </Button>
);

const FilterContent: React.FC<{
  searchInput?: string;
  applyFilter: () => void;
  setSearchInput: (searchTerm: string) => void;
  filterState: FilterState;
  toggleFilterState: (filterName: keyof FilterState, filterValue: string | number | null, enabled: boolean) => void;
}> = ({ searchInput, applyFilter, setSearchInput, filterState, toggleFilterState }) => {
  const [categoryIdsLimit, setCategoryIdsLimit] = useState(SHOW_MORE_INITIAL_LIMIT);
  const [subcategoryIdsLimit, setSubcategoryIdsLimit] = useState(SHOW_MORE_INITIAL_LIMIT);
  const [brandIdsLimit, setBrandIdsLimit] = useState(SHOW_MORE_INITIAL_LIMIT);

  const brandIds = useMemo(() => {
    return filterState?.BrandIds?.slice(0, brandIdsLimit);
  }, [filterState?.BrandIds, brandIdsLimit]);

  const categoryIds = useMemo(() => {
    return filterState?.CategoryIds?.slice(0, categoryIdsLimit);
  }, [filterState?.CategoryIds, categoryIdsLimit]);

  const subcategoryIds = useMemo(() => {
    return filterState?.SubcategoryIds?.slice(0, subcategoryIdsLimit);
  }, [filterState?.SubcategoryIds, subcategoryIdsLimit]);

  const categoryShowMore = categoryIdsLimit < (filterState?.CategoryIds?.length ?? 0);
  const subCategoryShowMore = subcategoryIdsLimit < (filterState?.SubcategoryIds?.length ?? 0);
  const brandShowMore = brandIdsLimit < (filterState?.BrandIds?.length ?? 0);
  const anyRewardChecked = !!filterState?.CampaignTypes?.find((type) => type?.enable);

  return (
    <FilterContentContainer>
      <div className="row mobile_search_bar">
        <TextField
          variant="standard"
          placeholder="Search products"
          value={searchInput}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              applyFilter();
            }
          }}
          onChange={(event) => setSearchInput(event.target.value)}
          inputProps={{ maxLength: 200 }}
          InputProps={{
            disableUnderline: true,
            startAdornment: <SearchIcon color="primary" sx={{ marginRight: 1.25, fontSize: 13 }} />,
          }}
          sx={(theme) => ({
            borderRadius: 17,
            height: 38,
            width: '100%',
            flex: 1,
            backgroundColor: brandclub_colors.blueWhite,
            '& div.MuiInput-root': {
              padding: '0 4px 0 14px',
              display: 'flex',
              alignItems: 'center',
              height: 38,
              '& input': {
                padding: 0,
                fontSize: 12,
                fontWeight: 500,
                fontFamily: 'Poppins',
                color: theme.palette.primary.main,
              },
            },
          })}
        />
      </div>
      {filterState?.CampaignTypes && (
        <FilterRow
          title="Rewards"
          options={filterState.CampaignTypes}
          onChange={(name, enabled) => toggleFilterState('CampaignTypes', name, enabled)}
          hideOptions={!anyRewardChecked}
        >
          <div className={`option_reward ${anyRewardChecked ? 'right' : ''}`}>
            <BrandclubFilterSwitch
              sx={{ margin: '2px 0px' }}
              checked={anyRewardChecked}
              onChange={(_, checked) => toggleFilterState('CampaignTypes', null, checked)}
            />
            <div className="toggle_text">Only show products with reward offers</div>
          </div>
        </FilterRow>
      )}
      {filterState?.RetailPrice && (
        <FilterRow
          title="Price"
          options={filterState.RetailPrice}
          onChange={(name, enabled) => toggleFilterState('RetailPrice', name, enabled)}
        />
      )}
      {filterState?.RetailerIds && (
        <FilterRow
          title="Stores"
          options={filterState.RetailerIds}
          onChange={(name, enabled) => toggleFilterState('RetailerIds', name, enabled)}
        />
      )}
      {brandIds && (
        <FilterRow
          title="Brands"
          options={brandIds}
          onChange={(name, enabled) => toggleFilterState('BrandIds', name, enabled)}
        >
          {brandShowMore && (
            <ShowMore
              onClick={() => {
                setBrandIdsLimit((prevLimit) => prevLimit + SHOW_MORE_LIMIT);
              }}
            />
          )}
        </FilterRow>
      )}
      {categoryIds && (
        <FilterRow
          title="Categories"
          options={categoryIds}
          onChange={(name, enabled) => toggleFilterState('CategoryIds', name, enabled)}
        >
          {categoryShowMore && (
            <ShowMore
              onClick={() => {
                setCategoryIdsLimit((prevLimit) => prevLimit + SHOW_MORE_LIMIT);
              }}
            />
          )}
        </FilterRow>
      )}
      {subcategoryIds && (
        <FilterRow
          title="Subcategories"
          options={subcategoryIds}
          onChange={(name, enabled) => toggleFilterState('SubcategoryIds', name, enabled)}
        >
          {subCategoryShowMore && (
            <ShowMore
              onClick={() => {
                setSubcategoryIdsLimit((prevLimit) => prevLimit + SHOW_MORE_LIMIT);
              }}
            />
          )}
        </FilterRow>
      )}
    </FilterContentContainer>
  );
};

export const FilterDialog: React.FC<{
  onCloseSearchDropdown?: () => void;
  searchInput?: string;
  setSearchInput: (searchTerm: string) => void;
  navigate: NavigateFunction;
  open: boolean;
  onClose: () => void;
  persistentQueryParams: BaseComponentPropsType['persistentQueryParams'];
  pageContext: BaseComponentPropsType['pageContext'];
  searchParams: URLSearchParams;
  axios: BaseComponentPropsType['axios'];
  routePrefix: string;
  brandId?: string | number;
}> = ({
  onCloseSearchDropdown,
  searchInput,
  setSearchInput,
  brandId,
  navigate,
  open,
  onClose,
  persistentQueryParams,
  searchParams,
  pageContext,
  axios,
  routePrefix,
}) => {
  const [originFilterState, setOriginFilterState] = useState<FilterState>({});
  const [filterState, setFilterState] = useState<FilterState>({});
  const desktopView = useMediaQuery(`(min-width:${TABLET_SCREEN_SIZE}px)`);

  useEffect(() => {
    const generateCurrentFilters = async () => {
      const searchRequestBody = searchParamsToSearchFilters(searchParams, pageContext, brandId);
      const facetsResult = await searchProductsRest(
        axios,
        searchRequestBody,
        { start: 0, size: 0 },
        {
          computeFacets: true,
          facetFieldNames: ['categoryId', 'retailerId', 'subCategoryId', 'brandId'],
        },
        undefined,
      );
      const originFilterState = {
        ...getStaticFilters(),
        ...getDynamicFilters(facetsResult),
      };
      const newFilterState = convertSearchParamToFilterState(searchParams, originFilterState);

      setOriginFilterState(originFilterState);
      setFilterState(newFilterState);
    };
    generateCurrentFilters();
  }, [searchParams, brandId]);

  const toggleFilterState = (filterName: keyof FilterState, filterValue: string | number | null, enabled: boolean) => {
    const filterStateClone = _cloneDeep(filterState);
    filterStateClone[filterName]?.forEach((option) => {
      if (filterValue === null || option.name === filterValue) {
        option.enable = enabled;
      }
    });
    setFilterState(filterStateClone);
  };

  const applyFilter = () => {
    let searchUrl = routePrefix + `/search?`;
    const searchParams = convertFilterStateToSearchParams(filterState);
    if (searchInput) {
      const encodedSearchInput = encodeURIComponent(searchInput.trim());
      searchUrl += `${FilterParam.TitleInclude}=${encodedSearchInput}`;
    }
    searchUrl += searchParams;
    if (persistentQueryParams) {
      searchUrl += `&${persistentQueryParams}`;
    }
    onCloseSearchDropdown?.();
    navigate(searchUrl);
    setFilterState({});
    onClose();
  };

  return (
    <Dialog
      fullScreen={!desktopView}
      TransitionComponent={desktopView ? undefined : Transition}
      onClose={onClose}
      PaperProps={{
        style: {
          height: desktopView ? 800 : '100%',
          maxWidth: 'unset',
          boxShadow: 'rgba(149, 157, 165, 0.2) 0px 8px 24px',
        },
      }}
      BackdropProps={{
        sx: {
          backgroundColor: brandclub_colors.darkBlue3,
          transition: 'opacity 225ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
        },
      }}
      style={{ zIndex: 1500 }}
      open={open}
    >
      <FilterDialogContainer>
        <FilterHeader>
          <div className="left">
            <CloseRoundedIcon color="primary" onClick={onClose} sx={{ cursor: 'pointer' }} />
          </div>
          <div className="center">Filter</div>
          <div
            className="right"
            role="button"
            style={{ cursor: 'pointer' }}
            onClick={() => {
              setFilterState(originFilterState);
              setSearchInput('');
            }}
          >
            Clear
          </div>
        </FilterHeader>
        <FilterContent
          searchInput={searchInput}
          setSearchInput={setSearchInput}
          applyFilter={applyFilter}
          filterState={filterState}
          toggleFilterState={toggleFilterState}
        />
        <FilterFooter>
          <button className="apply_button" onClick={applyFilter}>
            Apply
          </button>
        </FilterFooter>
      </FilterDialogContainer>
    </Dialog>
  );
};
